import React from 'react';
import { PieChart, CalendarDays, Info } from 'lucide-react';
import i18n from '../../../../../i18n';
import { OMR } from '../../../../common/icons';
import '../../../../../styles/components/financial-breakdown-card.scss';
import { STATUS_META } from './DeliveryStatusCard';

const fmt = (n, decimals = 2) =>
  new Intl.NumberFormat('en', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(n) || 0);

// "2026-10-01" -> "01 Oct 2026" (local date, never shifts a day)
const fmtDate = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(y, m - 1, d));
};

const getRangeLabel = (startDate, endDate) => {
  const from = fmtDate(startDate);
  const to = fmtDate(endDate);
  if (from && to) return `${from} – ${to}`;
  if (from) return i18n.t('pc_range_from', { date: from, defaultValue: 'From {{date}}' });
  if (to) return i18n.t('pc_range_until', { date: to, defaultValue: 'Until {{date}}' });
  return i18n.t('pc_range_all', { defaultValue: 'All time' });
};

/**
 * Financial breakdown of the selected status card:
 * bid amount + bid VAT + service charge (profit) + service charge VAT = total bidder amount.
 * Internal only – not rendered for clients.
 */
export const FinancialBreakdownCard = ({
  card,
  loading = false,
  startDate,
  endDate,
  decimals = 2,
}) => {
  const tone = (STATUS_META[card?.type] || STATUS_META.default).tone;

  const segments = [
    {
      key: 'bid',
      label: i18n.t('fb_bid_amount', { defaultValue: 'Bid amount' }),
      value: Number(card?.bidAmount) || 0,
    },
    {
      key: 'bidVat',
      label: i18n.t('fb_bid_vat', { defaultValue: 'Bid VAT' }),
      value: Number(card?.bidVat) || 0,
    },
    {
      key: 'profit',
      label: i18n.t('fb_service_charge', { defaultValue: 'Service charge' }),
      badge: i18n.t('fb_profit', { defaultValue: 'Profit' }),
      value: Number(card?.serviceCharge) || 0,
    },
    {
      key: 'scVat',
      label: i18n.t('fb_service_vat', { defaultValue: 'Service charge VAT' }),
      value: Number(card?.serviceVat) || 0,
    },
  ];

  const sum = segments.reduce((s, x) => s + x.value, 0);
  const total = Number(card?.bidderTotal) || sum;
  const pct = (v) => (total > 0 ? (v / total) * 100 : 0);
  const hasRange = Boolean(startDate || endDate);

  return (
    <section
      className="fb-card"
      style={{ '--fb-tone': tone }}
      aria-busy={loading}
      aria-label={i18n.t('fb_title', { defaultValue: 'Financial breakdown' })}
    >
      {/* head */}
      <div className="fb-head">
        <span className="fb-icon">
          <PieChart size={18} strokeWidth={2.2} />
        </span>
        <h3 className="fb-title">{i18n.t('fb_title', { defaultValue: 'Financial breakdown' })}</h3>
        {card?.title && <span className="fb-status">{card.title}</span>}

        <span className={`fb-range ${hasRange ? 'is-filtered' : ''}`}>
          <CalendarDays size={13} />
          {getRangeLabel(startDate, endDate)}
        </span>
      </div>

      {loading ? (
        <>
          <div className="fb-skel fb-skel--bar" />
          <div className="fb-grid">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="fb-skel fb-skel--cell" />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* composition bar */}
          <div
            className={`fb-bar ${total > 0 ? '' : 'is-empty'}`}
            role="img"
            aria-label={segments.map((s) => `${s.label} ${pct(s.value).toFixed(1)}%`).join(', ')}
          >
            {segments.map((s) =>
              s.value > 0 ? (
                <span
                  key={s.key}
                  className={`fb-seg fb-seg--${s.key}`}
                  style={{ width: `${pct(s.value)}%` }}
                  title={`${s.label}: ${pct(s.value).toFixed(1)}%`}
                />
              ) : null
            )}
          </div>

          {/* figures */}
          <div className="fb-grid">
            {segments.map((s) => (
              <div key={s.key} className={`fb-item fb-item--${s.key}`}>
                <div className="fb-item-label">
                  <i className={`fb-dot fb-dot--${s.key}`} />
                  <span>{s.label}</span>
                  {s.badge && <span className="fb-badge">{s.badge}</span>}
                </div>
                <div className="fb-item-value">
                  <OMR size={11} />
                  {fmt(s.value, decimals)}
                </div>
                <div className="fb-item-pct">
                  {pct(s.value).toFixed(1)}% {i18n.t('fb_of_total', { defaultValue: 'of total' })}
                </div>
              </div>
            ))}

            <div className="fb-item fb-item--total">
              <div className="fb-item-label">
                <span>{i18n.t('fb_total_bidder', { defaultValue: 'Total bidder amount' })}</span>
                <span
                  className="fb-info"
                  tabIndex={0}
                  data-tip={i18n.t('fb_total_hint', {
                    defaultValue: 'Bid + Bid VAT + Service charge + Service charge VAT',
                  })}
                >
                  <Info size={13} />
                </span>
              </div>
              <div className="fb-item-value">
                <OMR size={12} />
                {fmt(total, decimals)}
              </div>
              <div className="fb-item-pct">
                {card?.auctionCount ?? 0} {i18n.t('auctions')}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default FinancialBreakdownCard;
